import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output
} from '@angular/core';
import { FormioCustomComponent } from '@evi-ui/angular';
import { FormioEvent } from '@evi-ui/angular/elements.common';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as FileSaver from 'file-saver';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import { environment } from '../../../../environments/environment';
import { EventBusService } from '../../event-bus.service';
import { EventType } from '../../model/event.data';

/**
 * Displays the document identified by an "image id" (a CBS `documents` id) as an <img>.
 * By default the image id is this field's own bound value; set `imageIdField` to instead
 * read the id from elsewhere in the submission (e.g. "profile.photoDocumentId").
 *
 * Fetches from `apiUrl` (default: CBS's `{id}/preview` endpoint), with `${imageId}` in the
 * url replaced by the resolved image id, and reads the data URI from `responseDataProperty`
 * in the JSON response (default: `dataUri`, dotted paths supported). The optional download
 * button instead streams raw bytes from `downloadApiUrl` (default: CBS's `{id}/download`
 * endpoint, `${imageId}` supported the same way) for the browser to save.
 *
 * Formio redraws a field by tearing down and recreating its custom element, so a fresh
 * instance's constructor can receive the FORM_DATA_LOADED replay (it's a BehaviorSubject)
 * before Formio has bound `customOptions.imageIdField` onto it. `imageIdField` and the last
 * form data are therefore each cached independently and resolution is (re)attempted whenever
 * either one arrives, so whichever one is late still triggers the lookup.
 */
@Component({
  selector: 'image-by-id',
  templateUrl: './image-by-id.component.html',
  styleUrls: ['./image-by-id.component.scss']
})
export class ImageByIdComponent implements FormioCustomComponent<string> {
  destroyRef = inject(DestroyRef);

  @Input() key: string;
  @Input() disabled = false;
  @Output() formioEvent = new EventEmitter<FormioEvent>();

  private _imageIdField?: string;
  @Input()
  set imageIdField(field: string | undefined) {
    this._imageIdField = field;
    this.tryResolveFromFormData();
  }
  get imageIdField(): string | undefined {
    return this._imageIdField;
  }

  @Input() altText?: string;
  @Input() imageWidth?: string;
  @Input() emptyMessage?: string;
  @Input() apiUrl?: string;
  @Input() responseDataProperty?: string;
  @Input() showDownloadButton?: boolean;
  @Input() downloadApiUrl?: string;
  @Input() downloadButtonPosition?:
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
    | 'center';

  private _value: any;
  @Input()
  set value(val: any) {
    this._value = val;
    if (!this.imageIdField) {
      this.loadImage(val);
    }
  }
  get value(): any {
    return this._value;
  }
  @Output() valueChange = new EventEmitter<any>();

  imageSrc: SafeUrl | null = null;
  loading = false;
  errored = false;
  downloading = false;

  private currentImageId: any = null;
  private lastFormData: any = null;

  constructor(
    private readonly http: HttpClient,
    private readonly sanitizer: DomSanitizer,
    private readonly eventBusService: EventBusService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    this.eventBusService
      .on(EventType.FORM_DATA_LOADED)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((formData) => {
        if (!formData) {
          return;
        }
        this.lastFormData = formData;
        this.tryResolveFromFormData();
      });
  }

  private interpolateImageId(template: string, imageId: any): string {
    return template.replace(
      /\$\{imageId\}/g,
      encodeURIComponent(String(imageId))
    );
  }

  private buildPreviewUrl(imageId: any): string {
    const template =
      this.apiUrl ||
      `${environment.coreBaseApi}/documents/\${imageId}/preview`;
    return this.interpolateImageId(template, imageId);
  }

  private buildDownloadUrl(imageId: any): string {
    const template =
      this.downloadApiUrl ||
      `${environment.coreBaseApi}/documents/\${imageId}/download`;
    return this.interpolateImageId(template, imageId);
  }

  private tryResolveFromFormData(): void {
    if (!this.imageIdField || !this.lastFormData) {
      return;
    }
    this.loadImage(get(this.lastFormData, this.imageIdField));
  }

  private loadImage(imageId: any): void {
    if (imageId === this.currentImageId) {
      return;
    }
    this.currentImageId = imageId;
    this.imageSrc = null;
    this.errored = false;

    if (isNil(imageId) || imageId === '') {
      this.changeDetectorRef.detectChanges();
      return;
    }

    const url = this.buildPreviewUrl(imageId);
    this.loading = true;
    this.changeDetectorRef.detectChanges();
    this.http.get<Record<string, any>>(url).subscribe({
      next: (response) => {
        const dataUri = get(response, this.responseDataProperty || 'dataUri');
        this.imageSrc = this.sanitizer.bypassSecurityTrustUrl(dataUri);
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      },
      error: (error) => {
        console.warn('Unable to load image for document id', imageId, error);
        this.errored = true;
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  downloadImage(): void {
    const imageId = this.currentImageId;
    if (isNil(imageId) || imageId === '' || this.downloading) {
      return;
    }

    this.downloading = true;
    this.changeDetectorRef.detectChanges();
    this.http
      .get(this.buildDownloadUrl(imageId), {
        observe: 'response',
        responseType: 'blob'
      })
      .subscribe({
        next: (response) => {
          const filename =
            response.headers.get('downloadFileName') ||
            response.headers.get('fileName') ||
            `document-${imageId}`;
          FileSaver.saveAs(response.body as Blob, filename);
          this.downloading = false;
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          console.warn('Unable to download document id', imageId, error);
          this.downloading = false;
          this.changeDetectorRef.detectChanges();
        }
      });
  }
}
